import { useEffect, useRef, useId, useState } from "react";
import type { ReactNode } from "react";
import { X, AlertTriangle } from "lucide-react";
import { cn } from "../../lib/utils";

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason?: string) => void;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "danger" | "warning" | "info";
  icon?: ReactNode;
  reasonField?: { label: string; placeholder?: string };
  /**
   * When true (default) the dialog auto-closes after `onConfirm` returns.
   * Set to `false` for async mutations where the consumer needs the dialog
   * to stay visible until success/error feedback arrives — without this,
   * a `try { onConfirm } finally { onClose }` would close the dialog
   * BEFORE an async error could surface, silently breaking "stay open on
   * error" UX contracts (PolicyEngine delete pattern).
   */
  autoClose?: boolean;
}

const variantStyles = {
  danger: {
    iconBg: "bg-surface-2 border-[var(--badge-critical)]",
    iconColor: "text-[var(--badge-critical)]",
    // Solid red fill for FINAL confirmations — visually communicates
    // "irreversible action ahead". Standard .adp-btn-danger (outline)
    // is for routine delete buttons; the confirmation dialog earns
    // the stronger treatment. GitHub/Stripe/Linear convention.
    confirmBtn: "adp-btn-danger-solid",
  },
  warning: {
    iconBg: "bg-surface-2 border-[var(--badge-warning)]",
    iconColor: "text-[var(--badge-warning)]",
    confirmBtn: "adp-btn-warning",
  },
  info: {
    iconBg: "bg-surface-1 border-border",
    iconColor: "text-muted-foreground",
    confirmBtn: "adp-btn-primary",
  },
};

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "danger",
  icon,
  reasonField,
  autoClose = true,
}: ConfirmDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descId = useId();
  const reasonId = useId();
  const [reason, setReason] = useState("");

  // ESC + focus trap in one handler
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable || focusable.length === 0) { e.preventDefault(); return; }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) { e.preventDefault(); last.focus(); }
        } else {
          if (document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Move focus into dialog on open, restore to trigger on close
  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const timer = setTimeout(() => {
      const firstFocusable = dialogRef.current?.querySelector<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      (firstFocusable ?? dialogRef.current)?.focus();
    }, 0);
    return () => {
      clearTimeout(timer);
      previouslyFocused?.focus();
    };
  }, [isOpen]);

  // Reset reason when dialog closes
  useEffect(() => { if (!isOpen) setReason(""); }, [isOpen]);

  // Body scroll lock — prevents background page-scroll while modal open
  // (WAI-ARIA dialog pattern; matches every other modal primitive). Saves
  // previous overflow so closing this dialog doesn't unlock body-scroll
  // if a parent modal is still up (modal-on-modal stacking).
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    try {
      onConfirm(reason.trim() || undefined);
    } finally {
      // Skip auto-close for async-mutation consumers (PolicyEngine delete) —
      // they manage close in their own onSuccess / onError handlers so the
      // dialog stays visible until the outcome is known.
      if (autoClose) onClose();
    }
  };

  const styles = variantStyles[variant];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-50 animate-in fade-in-0 duration-200 motion-reduce:animate-none"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        tabIndex={-1}
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] max-w-[448px] outline-none animate-in fade-in-0 zoom-in-95 duration-200 motion-reduce:animate-none"
      >
        <div className="adp-modal">
          {/* Header */}
          <div className="flex items-start justify-between pb-4 border-b border-border">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              {/* Icon */}
              <div className={cn("p-2 border flex-shrink-0", styles.iconBg)}>
                {icon || <AlertTriangle className={cn("w-5 h-5", styles.iconColor)} />}
              </div>

              {/* Title + Description */}
              <div className="flex-1 min-w-0">
                <h2 id={titleId} className="text-lg font-semibold text-foreground break-words">
                  {title}
                </h2>
                <p id={descId} className="text-xs text-muted-foreground mt-0.5 break-words">
                  {description}
                </p>
              </div>
            </div>

            {/* Close Button */}
            <button
              type="button"
              onClick={onClose}
              className="adp-btn p-1 hover:bg-surface-2 border-transparent hover:border-border transition-colors motion-reduce:transition-none flex-shrink-0 ml-2"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>

          {/* Optional reason field */}
          {reasonField && (
            <div className="pt-4">
              <label htmlFor={reasonId} className="block text-[10px] text-mono text-muted-foreground uppercase tracking-wider mb-1.5">
                {reasonField.label}
              </label>
              <textarea
                id={reasonId}
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder={reasonField.placeholder ?? "Optional — will be recorded in the audit log."}
                maxLength={500}
                rows={3}
                className="w-full bg-surface-1 border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-brand transition-colors motion-reduce:transition-none resize-none p-2 rounded-none"
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="adp-btn px-4 py-2 bg-surface-1 hover:bg-surface-2 text-sm"
            >
              {cancelText}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className={cn("adp-btn px-4 py-2 text-sm font-semibold", styles.confirmBtn)}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
